import { Flag, Loader2, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StarRating } from '@/components/ui/star-rating';
import { use_public_reviews, use_report_review } from '@/features/review/hooks';

function fmt(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Public reviews list for a vendor detail page. Renders nothing when the vendor
 *  has no reviews — the page header already shows "No reviews yet". */
export function VendorReviewsSection({ vendor_id, review_count }: { vendor_id: string; review_count: number }) {
  const q = use_public_reviews(vendor_id);
  const report_m = use_report_review();
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];

  if (review_count === 0) return null;

  async function report(review_id: string) {
    const reason = window.prompt('Why are you reporting this review? (optional)') ?? '';
    await report_m.mutateAsync({ review_id, reason: reason.trim() || undefined });
    window.alert('Thanks — this review has been flagged for moderation.');
  }

  return (
    <Card data-testid="vendor-reviews">
      <CardHeader>
        <CardTitle>Reviews</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {q.isLoading ? (
          <div className="flex items-center justify-center p-8 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <ul className="divide-y-2 divide-brand-outline-variant/10" data-testid="reviews-list">
            {items.map((r) => (
              <li key={r.id} className="flex flex-col gap-2 p-4" data-testid={`review-${r.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <StarRating value={r.rating} size="sm" />
                    <span className="text-sm font-semibold">{r.reviewer_name}</span>
                    {r.pet_name && <span className="text-xs text-muted-foreground">for {r.pet_name}</span>}
                  </div>
                  <span className="shrink-0 text-xs text-muted-foreground">{fmt(r.created_at)}</span>
                </div>

                {r.comment && <p className="whitespace-pre-wrap text-sm">{r.comment}</p>}

                {r.vendor_reply && (
                  <div className="ml-3 rounded-md border-l-2 border-primary/40 bg-secondary/40 p-2.5 text-sm">
                    <p className="mb-0.5 flex items-center gap-1 text-xs font-semibold text-muted-foreground">
                      <MessageSquare className="h-3 w-3" /> Response from the vendor
                    </p>
                    <p className="whitespace-pre-wrap">{r.vendor_reply}</p>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => report(r.id)}
                  disabled={report_m.isPending}
                  data-testid={`report-review-${r.id}`}
                  className="inline-flex items-center gap-1 self-start text-xs font-medium text-muted-foreground transition-colors hover:text-destructive disabled:opacity-50"
                >
                  <Flag className="h-3 w-3" /> Report
                </button>
              </li>
            ))}
          </ul>
        )}

        {q.hasNextPage && (
          <div className="p-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => q.fetchNextPage()}
              disabled={q.isFetchingNextPage}
              data-testid="reviews-load-more"
            >
              {q.isFetchingNextPage ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Load more reviews'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
